import { useCallback, useEffect, useState } from "react";
import { fetchAvoidance } from "../lib/api";
import { RefreshCw } from "lucide-react";

const mmss = (s) => {
  const m = Math.floor((s || 0) / 60);
  return m ? `${m}m ${String((s || 0) % 60).padStart(2, "0")}s` : `${s || 0}s`;
};

/** What the subject goes quiet on. Silence weighted above slowness. */
export const Avoidance = ({ subject }) => {
  const [d, setD] = useState(null);

  const load = useCallback(
    () => fetchAvoidance(subject).then(setD).catch(() => setD(null)),
    [subject]
  );

  useEffect(() => {
    load();
  }, [load]);

  const rows = d?.rows || [];

  return (
    <div data-testid="admin-avoidance" className="space-y-8">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="label">Avoidance · 60% stalls, 40% answer latency</p>
          <p className="mt-2 max-w-2xl text-xs leading-relaxed text-zinc-500">
            Every stall is attributed to whatever was on the table when the subject went quiet. The
            Cartographer is told about the top three and instructed to approach them obliquely —
            never to confront the subject with the silence.
          </p>
        </div>
        <button onClick={load} className="label flex items-center gap-1.5 hover:text-zinc-200">
          <RefreshCw size={11} strokeWidth={1.6} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-px bg-[var(--line)] sm:grid-cols-4">
        {[
          ["Stall episodes", d?.total_stalls ?? 0],
          ["Total silence", mmss(d?.total_silence_seconds)],
          ["Topics touched", rows.length],
          ["Unmapped & avoided", rows.filter((r) => r.unfilled && r.stalls > 0).length],
        ].map(([k, v]) => (
          <div key={k} className="border border-[var(--line)] px-4 py-3">
            <p className="label">{k}</p>
            <p className="mt-1.5 font-data text-lg tabular-nums leading-none text-zinc-100">{v}</p>
          </div>
        ))}
      </div>

      <ul data-testid="avoidance-list">
        {rows.map((r) => (
          <li
            key={r.id}
            data-testid={`avoidance-row-${r.id}`}
            className="grid grid-cols-[1fr_auto] items-center gap-5 border-b border-[var(--line)] py-4"
          >
            <div className="min-w-0">
              <p className="truncate text-sm text-zinc-100">
                T{r.tier} {r.name}
                {r.stalls > 0 && (
                  <span className="ml-3 font-data text-[10px] uppercase tracking-[0.14em] text-zinc-400">
                    {r.stalls} stall{r.stalls === 1 ? "" : "s"} · {mmss(r.silence_seconds)}
                  </span>
                )}
                {r.unfilled && (
                  <span className="ml-3 font-data text-[10px] uppercase tracking-[0.14em] text-zinc-300">
                    unmapped
                  </span>
                )}
              </p>
              <p className="mt-1.5 truncate font-data text-[10px] text-zinc-600">
                {r.business_lens}
              </p>
              <div className="mt-2 h-[2px] w-full bg-[var(--line)]">
                <div
                  className="h-full bg-zinc-200"
                  style={{ width: `${Math.min(100, r.avoidance_index)}%` }}
                />
              </div>
            </div>
            <div className="text-right">
              <p className="font-data text-lg tabular-nums leading-none text-zinc-100">
                {r.avoidance_index}
              </p>
              <p className="mt-1.5 font-data text-[10px] text-zinc-600">
                heat {r.latency_heat} · sal {r.salience}
              </p>
            </div>
          </li>
        ))}
        {rows.length === 0 && (
          <p className="font-data text-[11px] text-zinc-600">
            Nothing avoided yet — no stalls recorded and no topic answered unusually slowly.
          </p>
        )}
      </ul>

      {d?.episodes?.length > 0 && (
        <div>
          <p className="label">Stall episodes</p>
          <ul className="mt-4" data-testid="avoidance-episodes">
            {[...d.episodes].reverse().map((e) => (
              <li key={`${e.t}-${e.seq}`} className="border-b border-[var(--line)] py-2.5">
                <p className="font-data text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                  {e.t?.slice(0, 16).replace("T", " ")} · {mmss(e.seconds)} silence · turn #{e.seq}
                  {e.mode ? ` · operator: ${e.mode}` : " · auto-detected"}
                </p>
                <p className="mt-1 text-xs text-zinc-400">
                  {e.categories?.length ? e.categories.join(" · ") : "no topic attributed"}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Avoidance;
